import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRobotContext } from './RobotContext';
import { GIF_ASSETS } from './utils';
export function RobotInline() {
    const { mode, targetElementId, images, imagesLoaded, onRobotClick, currentState, assetsBasePath, setState, } = useRobotContext();
    const [currentGif, setCurrentGif] = useState('');
    const standingIntervalRef = useRef(null);
    const typingStartTimeoutRef = useRef(null);
    const welcomeTimeoutRef = useRef(null);
    const thinkingTimeoutRef = useRef(null);
    const listeningStartTimeoutRef = useRef(null);
    const listeningEndTimeoutRef = useRef(null);
    const speakingStartTimeoutRef = useRef(null);
    const robotRef = useRef(null);
    const [targetElement, setTargetElement] = useState(null);
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        // 1. Try global document
        let el = document.getElementById(targetElementId || '');
        // 2. If not found, try searching in the current shadow root
        if (!el && robotRef.current) {
            const root = robotRef.current.getRootNode();
            if (root instanceof ShadowRoot) {
                el = root.getElementById(targetElementId || '');
            }
        }
        setTargetElement(el);
    }, [targetElementId, imagesLoaded]); // Re-run when images load to ensure element exists
    useEffect(() => {
        if (!imagesLoaded || !targetElement)
            return;
        const updateGif = () => {
            switch (currentState) {
                case 'welcome': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.welcome}`);
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    // After welcome completes, transition to standing
                    const welcomeGif = images.get(GIF_ASSETS.welcome);
                    if (welcomeGif) {
                        const duration = 5000; // 5 seconds for welcome animation
                        welcomeTimeoutRef.current = setTimeout(() => {
                            setState('standing');
                        }, duration);
                    }
                    else {
                        setState('standing');
                    }
                    break;
                }
                case 'standing': {
                    // Randomly alternate between standing_one and standing_two
                    const useOne = Math.random() < 0.5;
                    const gif = useOne ? GIF_ASSETS.standing_one : GIF_ASSETS.standing_two;
                    setCurrentGif(`${assetsBasePath}/${gif}`);
                    // Change standing gif every 5 seconds
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    standingIntervalRef.current = setInterval(() => {
                        const useOne = Math.random() < 0.5;
                        const gif = useOne ? GIF_ASSETS.standing_one : GIF_ASSETS.standing_two;
                        setCurrentGif(`${assetsBasePath}/${gif}`);
                    }, 5000);
                    break;
                }
                case 'thinking': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.thinking}`);
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    // After thinking completes, transition to standing
                    const thinkingGif = images.get(GIF_ASSETS.thinking);
                    if (thinkingGif) {
                        const duration = 5000; // 5 seconds for thinking animation
                        thinkingTimeoutRef.current = setTimeout(() => {
                            setState('standing');
                        }, duration);
                    }
                    break;
                }
                case 'typing-start': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.typing_start}`);
                    // After typing_start completes, transition to typing-loop
                    const startGif = images.get(GIF_ASSETS.typing_start);
                    if (startGif) {
                        const duration = 5000; // 5 seconds for typing_start animation
                        typingStartTimeoutRef.current = setTimeout(() => {
                            setState('typing-loop');
                        }, duration);
                    }
                    else {
                        setState('typing-loop');
                    }
                    break;
                }
                case 'typing-loop': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.typing_continuos}`);
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    break;
                }
                case 'listening-start': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.listening_start}`);
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    // After listening_start completes (1.92 sec), transition to listening-loop
                    const startGif = images.get(GIF_ASSETS.listening_start);
                    if (startGif) {
                        const duration = 1920; // 1.92 seconds for listening_start animation
                        listeningStartTimeoutRef.current = setTimeout(() => {
                            setState('listening-loop');
                        }, duration);
                    }
                    else {
                        setState('listening-loop');
                    }
                    break;
                }
                case 'listening-loop': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.listening_continous}`);
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    break;
                }
                case 'listening-end': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.listening_end}`);
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    // After listening_end completes (1.04 sec), transition to standing
                    const endGif = images.get(GIF_ASSETS.listening_end);
                    if (endGif) {
                        const duration = 1040; // 1.04 seconds for listening_end animation
                        listeningEndTimeoutRef.current = setTimeout(() => {
                            setState('standing');
                        }, duration);
                    }
                    else {
                        setState('standing');
                    }
                    break;
                }
                case 'speaking-start': {
                    // Immediately transition to speaking-loop
                    // Use setTimeout to avoid state update during render
                    speakingStartTimeoutRef.current = setTimeout(() => {
                        setState('speaking-loop');
                    }, 0);
                    break;
                }
                case 'speaking-loop': {
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.speaking}`);
                    if (standingIntervalRef.current) {
                        clearInterval(standingIntervalRef.current);
                    }
                    // speaking.gif loops continuously (5 seconds per loop)
                    break;
                }
                case 'speaking-end': {
                    // Transition to standing immediately
                    // Use setTimeout to avoid state update during render
                    speakingStartTimeoutRef.current = setTimeout(() => {
                        setState('standing');
                    }, 0);
                    break;
                }
                default:
                    setCurrentGif(`${assetsBasePath}/${GIF_ASSETS.standing_one}`);
            }
        };
        updateGif();
        return () => {
            if (standingIntervalRef.current) {
                clearInterval(standingIntervalRef.current);
            }
            if (typingStartTimeoutRef.current) {
                clearTimeout(typingStartTimeoutRef.current);
            }
            if (welcomeTimeoutRef.current) {
                clearTimeout(welcomeTimeoutRef.current);
            }
            if (thinkingTimeoutRef.current) {
                clearTimeout(thinkingTimeoutRef.current);
            }
            if (listeningStartTimeoutRef.current) {
                clearTimeout(listeningStartTimeoutRef.current);
            }
            if (listeningEndTimeoutRef.current) {
                clearTimeout(listeningEndTimeoutRef.current);
            }
            if (speakingStartTimeoutRef.current) {
                clearTimeout(speakingStartTimeoutRef.current);
            }
        };
    }, [currentState, imagesLoaded, images, targetElement, assetsBasePath, setState]);
    // Always render the ref div so we can access the Shadow Root if needed
    const refDiv = _jsx("div", { ref: robotRef, style: { display: 'none' } });
    if (!imagesLoaded) {
        return (_jsxs(_Fragment, { children: [refDiv, targetElement &&
                    createPortal(_jsx("div", { style: { textAlign: 'center', padding: '20px' } }), targetElement)] }));
    }
    if (!targetElement || !currentGif) {
        return refDiv;
    }
    if (mode !== 'inline' || !targetElementId) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [refDiv, createPortal(_jsx("img", { src: currentGif, alt: "Robot", onClick: onRobotClick, style: {
                    width: '262px',
                    height: '262px',
                    cursor: 'pointer',
                    display: 'block',
                    margin: '0 auto',
                } }), targetElement)] }));
}
